import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getPlaybookBaseUrl, getProjectsBaseUrl } from "../../../config/config";
import { sec } from "../../../utils/security";
import { AssemblyApiContext } from "../types/assembly-context";
import { AssemblyModel, AssemblyType } from "../types/assembly-model";
import { FlowGroupType, GroupModel } from "../types/group-model";
import { PromptModel, PromptDataType } from "../types/prompt-model";
import { AssemblyReferenceModel, AssemblyReferenceWriteModel } from "../types/assembly-reference";
import {
  AssemblyReferenceConditionWrite,
  AssemblyReferenceCategoryConditionWrite,
} from "../types/assembly-condition";
import { AssemblyReferenceTemplateColumnWrite } from "../types/assembly-template";
import { ReferenceValidationResult } from "../types/reference-validation";

// --- URL builders ---------------------------------------------------------
// The two backends differ in route shape; everything is derived from the
// AssemblyApiContext so callers never hand-build URLs.

/** Root for groups and assemblies. */
const sourceRoot = (ctx: AssemblyApiContext): string =>
  ctx.source === "flow"
    ? `${getProjectsBaseUrl()}/projects/${ctx.projectObjectIdentifier}/flows/${ctx.flowObjectIdentifier}`
    : `${getPlaybookBaseUrl()}/playbooks/${ctx.playbookObjectIdentifier}`;

const assemblyRoot = (ctx: AssemblyApiContext): string =>
  `${sourceRoot(ctx)}/assemblies/${ctx.assemblyObjectIdentifier}`;

const referencesRoot = (ctx: AssemblyApiContext): string => `${assemblyRoot(ctx)}/references`;

const referenceRoot = (ctx: AssemblyApiContext, referenceId: string): string =>
  `${referencesRoot(ctx)}/${referenceId}`;

const groupsUrl = (ctx: AssemblyApiContext): string =>
  ctx.source === "flow" ? `${sourceRoot(ctx)}/flow-groups` : `${sourceRoot(ctx)}/groups`;

/** Prompts are group-scoped. Flow prompts hang off the project (not the flow). */
const groupPromptsUrl = (ctx: AssemblyApiContext, groupId: string): string =>
  ctx.source === "flow"
    ? `${getProjectsBaseUrl()}/projects/${ctx.projectObjectIdentifier}/flow-groups/${groupId}/prompts`
    : `${getPlaybookBaseUrl()}/playbooks/${ctx.playbookObjectIdentifier}/groups/${groupId}/prompts`;

// --- Response normalisation ---------------------------------------------
// The two APIs return slightly different shapes (paginated vs bare arrays,
// differing field names, enum casing); normalise to the UI's models here.

/** Unwraps either a bare array or a { items: [] } pagination envelope. */
const unwrapList = (raw: any): any[] =>
  Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : [];

/** Maps a backend prompt data type (numeric or string) to the UI enum. */
const normalizePromptDataType = (value: unknown): PromptDataType => {
  if (typeof value === "number") return value as PromptDataType;
  // The backends serialise this enum with inconsistent casing (e.g. the Flow
  // prompts endpoint returns lowercase "boolean"/"categories"/"decimal"),
  // so match case-insensitively.
  switch (String(value).toLowerCase()) {
    case "boolean":
      return PromptDataType.Boolean;
    case "listofvalues":
    case "categories":
      return PromptDataType.ListOfValues;
    case "rangeofvalues":
    case "decimal":
      return PromptDataType.RangeOfValues;
    case "generative":
      return PromptDataType.Generative;
    case "date":
    case "datetime":
      return PromptDataType.Date;
    default:
      return PromptDataType.Text;
  }
};

// The backends serialise Toolkit `FlowGroupType` names (camelCase); those names
// differ from the frontend enum's string values, so map every known name onto
// the enum. `singleDocumentCollection` is a single-context group (one answer per
// collection) → treated like Bundle (per-document, no "list as", matching the
// server's own SingleDocumentCollection constraint); `multipleDocumentCollections`
// lists per document → Individually. Older pre-rename names are mapped too.
const FLOW_GROUP_TYPE_ALIASES: Record<string, FlowGroupType> = {
  // current Toolkit member names
  singleDocumentCollection: FlowGroupType.Bundle,
  multipleDocumentCollections: FlowGroupType.Individually,
  // pre-rename member names
  combinedDocuments: FlowGroupType.Bundle,
  individualDocuments: FlowGroupType.Individually,
  documentSets: FlowGroupType.Individually,
};

const normalizeFlowGroupType = (value: unknown): FlowGroupType | undefined => {
  if (typeof value !== "string") return undefined;
  const lower = value.charAt(0).toLowerCase() + value.slice(1);
  if (lower in FLOW_GROUP_TYPE_ALIASES) return FLOW_GROUP_TYPE_ALIASES[lower];
  return Object.values(FlowGroupType).includes(lower as FlowGroupType)
    ? (lower as FlowGroupType)
    : undefined;
};

const normalizeGroup = (raw: any): GroupModel => ({
  objectIdentifier: raw.objectIdentifier,
  name: raw.name,
  description: raw.description ?? undefined,
  orderNumber: raw.orderNumber,
  flowGroupType: normalizeFlowGroupType(raw.flowGroupType ?? raw.type),
});

const normalizePrompt = (raw: any): PromptModel => ({
  objectIdentifier: raw.objectIdentifier,
  label: raw.label ?? "",
  description: raw.description ?? "",
  question: raw.question ?? "",
  promptDataTypeId: normalizePromptDataType(raw.promptDataType ?? raw.promptDataTypeId),
});

const normalizeCondition = (raw: any, fallbackTargetPromptObjectIdentifier: string): any => ({
  ...raw,
  // Backend serialises this as `targetPromptObjectIdentifier`. Default to the
  // reference's own prompt for any pre-targetPrompt rows the backend might
  // still return during the rollout window.
  targetPromptObjectIdentifier:
    raw.targetPromptObjectIdentifier ?? fallbackTargetPromptObjectIdentifier,
});

const normalizeReference = (raw: any): AssemblyReferenceModel => {
  // Flow returns `prompts` (one-to-many); Playbook (and legacy Flow) return a
  // single `filter`/`flowPromptObjectIdentifier`. Normalise both to a list.
  const prompts =
    Array.isArray(raw.prompts) && raw.prompts.length > 0
      ? raw.prompts.map((p: any) => ({
          objectIdentifier: p.objectIdentifier,
          label: p.label ?? undefined,
          promptDataType: normalizePromptDataType(p.promptDataType ?? p.promptDataTypeId),
        }))
      : raw.filter
        ? [
            {
              objectIdentifier: raw.filter.objectIdentifier,
              label: raw.filter.label ?? undefined,
              promptDataType: normalizePromptDataType(raw.filter.promptDataType),
            },
          ]
        : raw.flowPromptObjectIdentifier
          ? [{ objectIdentifier: raw.flowPromptObjectIdentifier, label: raw.promptLabel }]
          : [];

  const promptObjectIdentifier = prompts[0]?.objectIdentifier ?? "";
  const filter = raw.filter
    ? normalizePrompt(raw.filter)
    : prompts[0]
      ? normalizePrompt({
          objectIdentifier: prompts[0].objectIdentifier,
          label: prompts[0].label,
          promptDataType: prompts[0].promptDataType,
        })
      : undefined;

  return {
    objectIdentifier: raw.objectIdentifier,
    stableObjectIdentifier: raw.stableObjectIdentifier,
    prompts,
    promptObjectIdentifier,
    promptLabel: prompts[0]?.label ?? raw.promptLabel,
    groupObjectIdentifier:
      raw.flowGroupObjectIdentifier ?? raw.playbookGroupObjectIdentifier ?? undefined,
    assemblyReferenceType: raw.assemblyReferenceType,
    multiDocumentListFormat: raw.multiDocumentListFormat ?? undefined,
    singleDocumentReferenceFormat: raw.singleDocumentReferenceFormat ?? undefined,
    singleDocumentAnswerListFormat: raw.singleDocumentAnswerListFormat ?? undefined,
    template: raw.template ?? null,
    templateColumns: unwrapList(raw.templateColumns ?? []),
    conditions: unwrapList(raw.conditions ?? []).map((c: any) =>
      normalizeCondition(c, promptObjectIdentifier)
    ),
    filter,
  };
};

const normalizeAssembly = (raw: any): AssemblyModel => ({
  objectIdentifier: raw.objectIdentifier,
  name: raw.name,
  description: raw.description ?? undefined,
  assemblyType: (raw.assemblyType ?? raw.type) as AssemblyType,
  // Playbook backend returns `playbookGroupObjectIdentifier`; flow backend
  // returns `flowGroupObjectIdentifier`.
  assignedGroupObjectIdentifier:
    raw.playbookGroupObjectIdentifier ?? raw.flowGroupObjectIdentifier ?? undefined,
});

// --- Request body builders ----------------------------------------------

/** Builds the source-specific create-reference body. */
const buildCreateReferenceBody = (ctx: AssemblyApiContext, model: AssemblyReferenceWriteModel) => {
  const common = {
    multiDocumentListFormat: model.multiDocumentListFormat,
    singleDocumentReferenceFormat: model.singleDocumentReferenceFormat,
    singleDocumentAnswerListFormat: model.singleDocumentAnswerListFormat,
    template: model.template ?? null,
    templateColumns: model.templateColumns ?? [],
    conditions: model.conditions ?? [],
  };
  if (ctx.source === "flow") {
    return {
      flowPromptObjectIdentifiers: model.promptObjectIdentifiers,
      assemblyReferenceType: model.assemblyReferenceType,
      ...common,
    };
  }
  return { filterObjectIdentifiers: model.promptObjectIdentifiers, ...common };
};

/**
 * Builds the source-specific update-reference body. Neither API accepts
 * conditions/template-columns on update — those are managed via their own
 * endpoints.
 */
const buildUpdateReferenceBody = (ctx: AssemblyApiContext, model: AssemblyReferenceWriteModel) => {
  const common = {
    multiDocumentListFormat: model.multiDocumentListFormat,
    singleDocumentReferenceFormat: model.singleDocumentReferenceFormat,
    singleDocumentAnswerListFormat: model.singleDocumentAnswerListFormat,
    template: model.template ?? null,
  };
  if (ctx.source === "flow") {
    return {
      flowPromptObjectIdentifiers: model.promptObjectIdentifiers,
      assemblyReferenceType: model.assemblyReferenceType,
      ...common,
    };
  }
  return { filterObjectIdentifiers: model.promptObjectIdentifiers, ...common };
};

export const playbookApi = createApi({
  reducerPath: "playbookApi",
  baseQuery: async (args, api, extraOptions) =>
    fetchBaseQuery({
      baseUrl: "",
      prepareHeaders: async (headers) => {
        const token = await sec.getAccessTokenSilently()();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        return headers;
      },
    })(args, api, extraOptions),
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 2,
  tagTypes: ["Reference"],
  endpoints: (builder) => ({
    // --- Assembly ---------------------------------------------------------
    getAssembly: builder.query<AssemblyModel, AssemblyApiContext>({
      query: (ctx) => ({ url: assemblyRoot(ctx) }),
      transformResponse: normalizeAssembly,
    }),

    saveAssembly: builder.mutation<void, { ctx: AssemblyApiContext; docxFile: File }>({
      query: ({ ctx, docxFile }) => {
        const formData = new FormData();
        formData.append("file", docxFile);
        return { url: `${assemblyRoot(ctx)}/content`, method: "PUT", body: formData };
      },
    }),

    // --- Groups & prompts -------------------------------------------------
    getGroups: builder.query<GroupModel[], AssemblyApiContext>({
      query: (ctx) => ({ url: groupsUrl(ctx), params: { page: 1, pageSize: 200 } }),
      transformResponse: (raw: any) => unwrapList(raw).map(normalizeGroup),
    }),

    getGroupPrompts: builder.query<
      PromptModel[],
      { ctx: AssemblyApiContext; groupObjectIdentifier: string; searchText: string }
    >({
      query: ({ ctx, groupObjectIdentifier, searchText }) => ({
        url: groupPromptsUrl(ctx, groupObjectIdentifier),
        params: {
          searchText,
          searchTerm: searchText,
          flatten: true,
          pageSize: 100,
          pageNumber: 1,
          take: 100,
        },
      }),
      transformResponse: (raw: any) => unwrapList(raw).map(normalizePrompt),
    }),

    // --- References -------------------------------------------------------
    getReferences: builder.query<
      AssemblyReferenceModel[],
      { ctx: AssemblyApiContext; searchText?: string }
    >({
      query: ({ ctx }) => ({ url: referencesRoot(ctx), params: { page: 1, pageSize: 200 } }),
      transformResponse: (raw: any) => unwrapList(raw).map(normalizeReference),
      providesTags: ["Reference"],
    }),

    getReferenceById: builder.query<
      AssemblyReferenceModel,
      { ctx: AssemblyApiContext; referenceId: string }
    >({
      query: ({ ctx, referenceId }) => ({ url: referenceRoot(ctx, referenceId) }),
      transformResponse: normalizeReference,
      providesTags: ["Reference"],
    }),

    addReference: builder.mutation<
      AssemblyReferenceModel,
      { ctx: AssemblyApiContext; model: AssemblyReferenceWriteModel }
    >({
      query: ({ ctx, model }) => ({
        url: referencesRoot(ctx),
        method: "POST",
        body: buildCreateReferenceBody(ctx, model),
      }),
      transformResponse: normalizeReference,
      invalidatesTags: ["Reference"],
    }),

    updateReference: builder.mutation<
      AssemblyReferenceModel,
      { ctx: AssemblyApiContext; referenceId: string; model: AssemblyReferenceWriteModel }
    >({
      query: ({ ctx, referenceId, model }) => ({
        url: referenceRoot(ctx, referenceId),
        method: "PUT",
        body: buildUpdateReferenceBody(ctx, model),
      }),
      transformResponse: normalizeReference,
      invalidatesTags: ["Reference"],
    }),

    deleteReference: builder.mutation<void, { ctx: AssemblyApiContext; referenceId: string }>({
      query: ({ ctx, referenceId }) => ({
        url: referenceRoot(ctx, referenceId),
        method: "DELETE",
      }),
      invalidatesTags: ["Reference"],
    }),

    validateReferences: builder.mutation<
      ReferenceValidationResult[],
      { ctx: AssemblyApiContext; referenceIds: string[] }
    >({
      query: ({ ctx, referenceIds }) => ({
        url: `${referencesRoot(ctx)}/validate`,
        method: "POST",
        body: { stableObjectIdentifiers: referenceIds },
      }),
    }),

    // --- Conditions -------------------------------------------------------
    addCondition: builder.mutation<
      unknown,
      { ctx: AssemblyApiContext; referenceId: string; condition: AssemblyReferenceConditionWrite }
    >({
      query: ({ ctx, referenceId, condition }) => ({
        url: `${referenceRoot(ctx, referenceId)}/conditions`,
        method: "POST",
        body: condition,
      }),
      invalidatesTags: ["Reference"],
    }),

    updateCondition: builder.mutation<
      unknown,
      {
        ctx: AssemblyApiContext;
        referenceId: string;
        conditionId: string;
        condition: AssemblyReferenceConditionWrite;
      }
    >({
      query: ({ ctx, referenceId, conditionId, condition }) => ({
        url: `${referenceRoot(ctx, referenceId)}/conditions/${conditionId}`,
        method: "PUT",
        body: condition,
      }),
      invalidatesTags: ["Reference"],
    }),

    deleteCondition: builder.mutation<
      void,
      { ctx: AssemblyApiContext; referenceId: string; conditionId: string }
    >({
      query: ({ ctx, referenceId, conditionId }) => ({
        url: `${referenceRoot(ctx, referenceId)}/conditions/${conditionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Reference"],
    }),

    // --- Category conditions ---------------------------------------------
    addCategoryCondition: builder.mutation<
      unknown,
      {
        ctx: AssemblyApiContext;
        referenceId: string;
        conditionId: string;
        categoryCondition: AssemblyReferenceCategoryConditionWrite;
      }
    >({
      query: ({ ctx, referenceId, conditionId, categoryCondition }) => ({
        url: `${referenceRoot(ctx, referenceId)}/conditions/${conditionId}/category-conditions`,
        method: "POST",
        body: categoryCondition,
      }),
      invalidatesTags: ["Reference"],
    }),

    updateCategoryCondition: builder.mutation<
      unknown,
      {
        ctx: AssemblyApiContext;
        referenceId: string;
        conditionId: string;
        categoryConditionId: string;
        categoryCondition: AssemblyReferenceCategoryConditionWrite;
      }
    >({
      query: ({ ctx, referenceId, conditionId, categoryConditionId, categoryCondition }) => ({
        url: `${referenceRoot(ctx, referenceId)}/conditions/${conditionId}/category-conditions/${categoryConditionId}`,
        method: "PUT",
        body: categoryCondition,
      }),
      invalidatesTags: ["Reference"],
    }),

    deleteCategoryCondition: builder.mutation<
      void,
      {
        ctx: AssemblyApiContext;
        referenceId: string;
        conditionId: string;
        categoryConditionId: string;
      }
    >({
      query: ({ ctx, referenceId, conditionId, categoryConditionId }) => ({
        url: `${referenceRoot(ctx, referenceId)}/conditions/${conditionId}/category-conditions/${categoryConditionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Reference"],
    }),

    // --- Template columns -------------------------------------------------
    addTemplateColumn: builder.mutation<
      unknown,
      { ctx: AssemblyApiContext; referenceId: string; column: AssemblyReferenceTemplateColumnWrite }
    >({
      query: ({ ctx, referenceId, column }) => ({
        url: `${referenceRoot(ctx, referenceId)}/template-columns`,
        method: "POST",
        body: column,
      }),
      invalidatesTags: ["Reference"],
    }),

    updateTemplateColumn: builder.mutation<
      unknown,
      {
        ctx: AssemblyApiContext;
        referenceId: string;
        columnId: string;
        column: AssemblyReferenceTemplateColumnWrite;
      }
    >({
      query: ({ ctx, referenceId, columnId, column }) => ({
        url: `${referenceRoot(ctx, referenceId)}/template-columns/${columnId}`,
        method: "PUT",
        body: column,
      }),
      invalidatesTags: ["Reference"],
    }),

    deleteTemplateColumn: builder.mutation<
      void,
      { ctx: AssemblyApiContext; referenceId: string; columnId: string }
    >({
      query: ({ ctx, referenceId, columnId }) => ({
        url: `${referenceRoot(ctx, referenceId)}/template-columns/${columnId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Reference"],
    }),
  }),
});

export const {
  useGetAssemblyQuery,
  useSaveAssemblyMutation,
  useGetGroupsQuery,
  useGetGroupPromptsQuery,
  useGetReferencesQuery,
  useGetReferenceByIdQuery,
  useAddReferenceMutation,
  useUpdateReferenceMutation,
  useDeleteReferenceMutation,
  useValidateReferencesMutation,
  useAddConditionMutation,
  useUpdateConditionMutation,
  useDeleteConditionMutation,
  useAddCategoryConditionMutation,
  useUpdateCategoryConditionMutation,
  useDeleteCategoryConditionMutation,
  useAddTemplateColumnMutation,
  useUpdateTemplateColumnMutation,
  useDeleteTemplateColumnMutation,
} = playbookApi;
